import { useIntl } from 'react-intl';
import { Alert, AlertTitle, Box, Typography } from '@rossum/ui/material';
import { formatViolations } from '../formatViolations';
import { DatasetColumn } from '../types';
import { UploadFailure } from './useFollowedUpload';

// The rest are only counted, so the dialog stays short.
const MAX_SHOWN_PROBLEMS = 5;

type ImportProblemsProps = {
  failure: UploadFailure;
  columns: DatasetColumn[];
};

// Why a file couldn't be imported, under the area it is dropped on again.
export const ImportProblems = ({ failure, columns }: ImportProblemsProps) => {
  const intl = useIntl();
  const violations = failure.kind === 'file' ? failure.violations : [];
  const problems = formatViolations(intl, violations, columns);
  const hidden = problems.length - MAX_SHOWN_PROBLEMS;

  return (
    <Alert
      severity="error"
      variant="outlined"
      data-cy="dataset-import-problems"
    >
      <AlertTitle>
        {problems.length > 0
          ? intl.formatMessage({
              id: 'features.datasets.import.problems.title',
            })
          : intl.formatMessage({ id: 'features.datasets.import.failed' })}
      </AlertTitle>
      {failure.kind === 'file' ? (
        <>
          {problems.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {problems.slice(0, MAX_SHOWN_PROBLEMS).map(problem => (
                <li key={problem}>{problem}</li>
              ))}
              {hidden > 0 ? (
                <li>
                  {intl.formatMessage(
                    { id: 'features.datasets.import.problems.more' },
                    { count: hidden }
                  )}
                </li>
              ) : null}
            </Box>
          ) : (
            failure.message
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {intl.formatMessage({
              id: 'features.datasets.import.problems.fix',
            })}
          </Typography>
        </>
      ) : (
        (failure.message ??
        intl.formatMessage({
          id: 'features.datasets.notification.importFailed',
        }))
      )}
    </Alert>
  );
};
